// frontend/src/components/Layout/Layout.jsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Layout as AntLayout, Space, message, Drawer, Alert } from 'antd';
import { ClockCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../Sidebar/Sidebar';
import UserDropdown from '../UserDropdown/UserDropdown';
import NotificationsBell from '../NotificationsBell/NotificationsBell';
import WorkspaceSwitcher from '../WorkspaceSwitcher/WorkspaceSwitcher';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import PwaInstallBanner from '../PwaInstallBanner/PwaInstallBanner';
import QuickAddFab from '../QuickAddFab/QuickAddFab';
import MobileBottomNav from '../MobileBottomNav/MobileBottomNav';
import { usePwaInstall } from '../../pwa/usePwaInstall';
import './Layout.css';

import TransacaoModal from '../TransacaoModal/TransacaoModal';
import SelecaoCartaoModal from '../SelecaoCartaoModal/SelecaoCartaoModal';
import CartaoDespesaModal from '../CartaoDespesaModal/CartaoDespesaModal';
import TransacaoMilhasModal from '../TransacaoMilhasModal/TransacaoMilhasModal';
import SaidaMilhasModal from '../SaidaMilhasModal/SaidaMilhasModal';
import TransferenciaMilhasModal from '../TransferenciaMilhasModal/TransferenciaMilhasModal';
import TransferenciaTerceirosModal from '../TransferenciaTerceirosModal/TransferenciaTerceirosModal';

import { getContas } from '../../services/accountService';
import { getCategorias } from '../../services/categoryService';

const { Header, Content } = AntLayout;

const Layout = ({ children }) => {
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const { user, temModuloFinanceiro, temModuloMilhas } = useAuth();
    const { workspaceAtivo, trocarWorkspace } = useWorkspace();
    const {
        canPromptInstall,
        isIosSafari,
        isStandalone,
        promptInstall,
        openIosGuide,
    } = usePwaInstall();

    const trialInfo = useMemo(() => {
        if (!user?.data_fim_trial || user?.plano_financeiro_ativo || user?.plano_milhas_ativo) return null;
        const fim = new Date(user.data_fim_trial);
        if (fim <= new Date()) return null;
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        fim.setHours(0, 0, 0, 0);
        const dias = Math.ceil((fim - hoje) / (1000 * 60 * 60 * 24));
        const texto = dias === 1
            ? 'Último dia do seu teste grátis'
            : `${dias} dias restantes do seu teste grátis`;
        return { dias, texto, dataFim: user.data_fim_trial, urgente: dias <= 5 };
    }, [user?.data_fim_trial, user?.plano_financeiro_ativo, user?.plano_milhas_ativo]);

    // Desktop: controla se está expandido (true) ou colapsado (false)
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Mobile: controla se o Drawer está visível (true) ou oculto (false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Hook de mensagem para garantir feedback global
    const [messageApi, contextHolder] = message.useMessage();

    // Estados dos Modais
    const [isTransacaoModalOpen, setIsTransacaoModalOpen] = useState(false);
    const [isSelecaoCartaoModalOpen, setIsSelecaoCartaoModalOpen] = useState(false);
    const [isCartaoDespesaModalOpen, setIsCartaoDespesaModalOpen] = useState(false);
    const [isEntradaMilhasModalOpen, setIsEntradaMilhasModalOpen] = useState(false);
    const [isSaidaMilhasModalOpen, setIsSaidaMilhasModalOpen] = useState(false);
    const [isTransfProgramasModalOpen, setIsTransfProgramasModalOpen] = useState(false);
    const [isTransfPessoasModalOpen, setIsTransfPessoasModalOpen] = useState(false);

    const [selectedCardId, setSelectedCardId] = useState(null);

    // Estados de Dados para o TransacaoModal Global
    const [contas, setContas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    const { triggerRefresh } = useDataRefresh();

    // Referência para o container de conteúdo rolável
    const contentRef = useRef(null);
    const location = useLocation();

    // Efeito para rolar o conteúdo para o topo sempre que a rota mudar
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo(0, 0);
        }
    }, [location.pathname]);

    // Teclado virtual / visualViewport — ajusta --vvh e offset para modais fullscreen
    useEffect(() => {
        if (!isMobile || typeof window === 'undefined' || !window.visualViewport) return undefined;

        let rafId = 0;
        const KEYBOARD_THRESHOLD = 120;

        const syncVvh = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const vv = window.visualViewport;
                if (!vv) return;
                const h = Math.round(vv.height);
                const offset = Math.round(vv.offsetTop || 0);
                if (h > 0) {
                    document.documentElement.style.setProperty('--vvh', `${h}px`);
                }
                document.documentElement.style.setProperty('--vv-offset', `${offset}px`);

                const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_THRESHOLD;
                document.querySelectorAll('.mobile-fullscreen-modal').forEach((el) => {
                    el.classList.toggle('keyboard-open', keyboardOpen);
                });
            });
        };

        syncVvh();
        // Só resize — scroll do visualViewport causa thrash/saltos no iOS
        window.visualViewport.addEventListener('resize', syncVvh);
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            window.visualViewport.removeEventListener('resize', syncVvh);
            document.documentElement.style.removeProperty('--vvh');
            document.documentElement.style.removeProperty('--vv-offset');
            document.querySelectorAll('.mobile-fullscreen-modal').forEach((el) => {
                el.classList.remove('keyboard-open');
            });
        };
    }, [isMobile]);

    useEffect(() => {
        if (isTransacaoModalOpen && temModuloFinanceiro) {
            const loadModalData = async () => {
                try {
                    setLoadingData(true);

                    const [contasData, categoriasData] = await Promise.all([
                        getContas(),
                        getCategorias()
                    ]);

                    setContas(contasData.results || contasData || []);
                    setCategorias(categoriasData || []);

                } catch (error) {
                    console.error("Erro ao carregar dados para o modal global:", error);
                } finally {
                    setLoadingData(false);
                }
            };
            loadModalData();
        }
    }, [isTransacaoModalOpen, temModuloFinanceiro]);

    const handleOpenSelecaoCartao = () => {
        setIsSelecaoCartaoModalOpen(true);
    };

    const handleCartaoSelecionado = (cartaoId) => {
        setSelectedCardId(cartaoId);
        setIsSelecaoCartaoModalOpen(false);
        setIsCartaoDespesaModalOpen(true);
    };

    // ===================================================================================
    // CORREÇÃO: Recebe 'successMessage' para exibir o feedback visual através do Layout
    // ===================================================================================
    const handleModalSuccess = (shouldClose = true, successMessage = null) => {
        // 1. Dispara atualização do contexto
        triggerRefresh();

        // 2. Dispara evento global para forçar atualização de listagens
        window.dispatchEvent(new Event('transaction-updated'));

        // 3. Exibe a mensagem de sucesso (se houver) usando o contexto global do Layout
        if (successMessage && typeof successMessage === 'string') {
            messageApi.success(successMessage);
        }

        // 4. Gerencia o fechamento da modal
        if (shouldClose) {
            setIsTransacaoModalOpen(false);
            setIsSelecaoCartaoModalOpen(false);
            setIsCartaoDespesaModalOpen(false);
            setIsEntradaMilhasModalOpen(false);
            setIsSaidaMilhasModalOpen(false);
            setIsTransfProgramasModalOpen(false);
            setIsTransfPessoasModalOpen(false);
        }
    };

    const openMilhasModal = (setter) => {
        setter(true);
        if (isMobile) setIsDrawerOpen(false);
    };

    const toggleSidebar = () => {
        if (isMobile) {
            setIsDrawerOpen(!isDrawerOpen);
        } else {
            setIsSidebarOpen(!isSidebarOpen);
        }
    };

    // Componente Sidebar comum para reuso
    const renderSidebar = () => (
        <Sidebar
            isOpen={isMobile ? true : isSidebarOpen} // No mobile, se o drawer abriu, o sidebar conteúdo está "aberto"
            onToggle={toggleSidebar} // Passa a função de toggle genérica
            onOpenTransacaoModal={() => { setIsTransacaoModalOpen(true); if (isMobile) setIsDrawerOpen(false); }}
            onOpenSelecaoCartaoModal={() => { handleOpenSelecaoCartao(); if (isMobile) setIsDrawerOpen(false); }}
            onOpenEntradaMilhas={() => openMilhasModal(setIsEntradaMilhasModalOpen)}
            onOpenSaidaMilhas={() => openMilhasModal(setIsSaidaMilhasModalOpen)}
            onOpenTransfProgramas={() => openMilhasModal(setIsTransfProgramasModalOpen)}
            onOpenTransfPessoas={() => openMilhasModal(setIsTransfPessoasModalOpen)}
            isMobile={isMobile}
        />
    );

    return (
        <AntLayout style={{ height: '100dvh', position: 'relative' }}>
            {contextHolder} {/* Renderiza o contexto de mensagens */}

            {/* Desktop Sidebar (Sider) */}
            {!isMobile && renderSidebar()}

            {/* Mobile Sidebar (Drawer) */}
            {isMobile && (
                <Drawer
                    placement="left"
                    onClose={() => setIsDrawerOpen(false)}
                    open={isDrawerOpen}
                    width={250}
                    styles={{
                        body: {
                            padding: 0,
                            paddingTop: 'var(--safe-top, env(safe-area-inset-top, 0px))',
                        },
                    }}
                    className="app-sidebar-drawer"
                    rootClassName="app-sidebar-drawer"
                >
                    {renderSidebar()}
                </Drawer>
            )}

            <AntLayout>
                <Header
                    className={isMobile ? 'app-layout-header' : undefined}
                    style={{
                        padding: isMobile ? '0 16px' : '0 24px',
                        background: 'var(--color-background, #fff)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: isMobile ? undefined : 64,
                    }}
                >
                    <div className="app-layout-header-inner">
                        {isMobile ? (
                            <span className="app-layout-header-brand" aria-hidden="true">
                                Saldo Ativo
                            </span>
                        ) : (
                            <div />
                        )}

                        <Space align="center" size={isMobile ? 'middle' : 'large'}>
                            <WorkspaceSwitcher isMobile={isMobile} />
                            <NotificationsBell isMobile={isMobile} />
                            <UserDropdown isMobile={isMobile} />
                        </Space>
                    </div>
                </Header>
                {workspaceAtivo && (
                    <Alert
                        message={
                            <Space size="small" wrap>
                                <TeamOutlined />
                                <span>
                                    Você está na conta de <strong>{workspaceAtivo.titular?.nome}</strong>.
                                </span>
                                <a
                                    onClick={(e) => { e.preventDefault(); trocarWorkspace(null); }}
                                    href="/meu-desempenho"
                                    style={{ fontWeight: 600 }}
                                >
                                    Voltar para a minha
                                </a>
                            </Space>
                        }
                        type="warning"
                        showIcon={false}
                        banner
                        style={{ margin: 0, borderRadius: 0, textAlign: 'center' }}
                    />
                )}
                {trialInfo && !workspaceAtivo && (
                    <Alert
                        message={
                            <Space size="small" wrap>
                                <ClockCircleOutlined />
                                <span>{trialInfo.texto}.</span>
                                <Link to="/checkout" style={{ fontWeight: 600 }}>
                                    Assinar agora
                                </Link>
                            </Space>
                        }
                        type={trialInfo.urgente ? 'warning' : 'info'}
                        showIcon={false}
                        style={{ margin: 0, borderRadius: 0, textAlign: 'center' }}
                    />
                )}
                <PwaInstallBanner />
                <Content
                    ref={contentRef}
                    className={isMobile ? 'app-layout-content--mobile' : undefined}
                    style={{
                        padding: isMobile ? undefined : '2rem',
                        backgroundColor: 'var(--color-background, #fff)',
                        overflowY: 'auto',
                    }}
                >
                    {children}
                </Content>
            </AntLayout>

            {isMobile && (
                <>
                    <MobileBottomNav onOpenMore={() => setIsDrawerOpen(true)} />
                    <QuickAddFab
                        temModuloFinanceiro={temModuloFinanceiro}
                        temModuloMilhas={temModuloMilhas}
                        onOpenTransacao={() => setIsTransacaoModalOpen(true)}
                        onOpenCartao={handleOpenSelecaoCartao}
                        onOpenEntradaMilhas={() => setIsEntradaMilhasModalOpen(true)}
                        onOpenSaidaMilhas={() => setIsSaidaMilhasModalOpen(true)}
                        onOpenTransfProgramas={() => setIsTransfProgramasModalOpen(true)}
                        onOpenTransfPessoas={() => setIsTransfPessoasModalOpen(true)}
                        canPromptInstall={canPromptInstall}
                        onInstallApp={promptInstall}
                        onShowIosGuide={openIosGuide}
                        isIosSafari={isIosSafari && !isStandalone}
                    />
                </>
            )}

            {/* Modal Global de Lançamento Financeiro */}
            {temModuloFinanceiro && (
            <>
            <TransacaoModal
                isOpen={isTransacaoModalOpen}
                onClose={() => setIsTransacaoModalOpen(false)}
                onSave={handleModalSuccess}
                contas={contas}
                allCategorias={categorias}
                isLoadingData={loadingData}
                isMobile={isMobile}
            />

            <SelecaoCartaoModal
                isOpen={isSelecaoCartaoModalOpen}
                onClose={() => setIsSelecaoCartaoModalOpen(false)}
                onSelect={handleCartaoSelecionado}
                isMobile={isMobile}
            />

            {selectedCardId && (
                <CartaoDespesaModal
                    isOpen={isCartaoDespesaModalOpen}
                    onClose={() => {
                        setIsCartaoDespesaModalOpen(false);
                        setSelectedCardId(null);
                    }}
                    onSave={handleModalSuccess}
                    cartaoId={selectedCardId}
                    isMobile={isMobile}
                />
            )}
            </>
            )}

            {temModuloMilhas && (
            <>
            <TransacaoMilhasModal
                visible={isEntradaMilhasModalOpen}
                onClose={() => setIsEntradaMilhasModalOpen(false)}
                onSuccess={handleModalSuccess}
                isMobile={isMobile}
            />
            <SaidaMilhasModal
                visible={isSaidaMilhasModalOpen}
                onClose={() => setIsSaidaMilhasModalOpen(false)}
                onSuccess={handleModalSuccess}
                isMobile={isMobile}
            />
            <TransferenciaMilhasModal
                visible={isTransfProgramasModalOpen}
                onClose={() => setIsTransfProgramasModalOpen(false)}
                onSuccess={handleModalSuccess}
                isMobile={isMobile}
            />
            <TransferenciaTerceirosModal
                visible={isTransfPessoasModalOpen}
                onClose={() => setIsTransfPessoasModalOpen(false)}
                onSuccess={handleModalSuccess}
                isMobile={isMobile}
            />
            </>
            )}
        </AntLayout>
    );
};

export default Layout;